import React from "react";
import styled from "styled-components";

import TextInput from "components/inputs/text-input";
import Dropdown, { IDropItem } from "components/inputs/dropdown";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import DragNDrop from "components/inputs/dragNdrop";
import { MusicGenres } from "constants/genres";
import { Languages } from "constants/languages";
import { IMusicData, MusicDataKeys } from "types/musicData";
import GrayCard from "components/gray-card";
import { GetUser, GetUserArtists } from "services/user/getUser";
import TextDropdown from "components/inputs/text-dropdown";

interface IProps {
  handleChange: (key: MusicDataKeys, value: any) => void;
  musicData: IMusicData;
}
const StepOne = ({ handleChange, musicData }: IProps) => {
  const UserArtists = GetUserArtists();

  const ArtistItems = () => {
    let temp = GetUserArtists();
    let data: (string | IDropItem)[] = ["None"];
    for (let i = 0; i < temp.length; i++) {
      data.push({
        label: temp[i].name,
        icon: (
          <ArtistIcon>
            <Typography size="s" color={Colors.white200}>
              {temp[i].name[0]}
            </Typography>
          </ArtistIcon>
        )
      });
    }
    return data;
  };

  return (
    <Container>
      <GrayCard title="Release Information">
        <TextInput
          label="Release Title"
          placeholder="Enter your Single, Album or EP name"
          value={musicData.releaseTitle}
          onChange={(e) => handleChange("releaseTitle", e.target.value)}
        />
        <Spacer />
        <TextInput
          label="Record Label"
          placeholder="name"
          value={musicData.recordLabel}
          onChange={(e) => handleChange("recordLabel", e.target.value)}
          disabled={GetUser()?.subscription === "free" ? true : false}
          lockTip="upgrade"
        />
        <Spacer />
        <Typography mb="0.4" weight="medium" color={Colors.grey400}>
          Type of Release
        </Typography>
        <Dropdown
          items={["Single", "EP", "Album"]}
          chosenItem={musicData.releaseType}
          selectItem={(value) => handleChange("releaseType", value)}
        />
        <Spacer />
        <Typography mb="0.4" weight="medium" color={Colors.grey400}>
          Primary Artist
        </Typography>
        <Dropdown
          items={ArtistItems()}
          chosenItem={musicData.primaryArtist.name}
          selectItem={(value) => {
            for (let i = 0; i < UserArtists.length; i++) {
              const artist = UserArtists[i];
              if (artist.name === value) {
                handleChange("primaryArtist", artist);
              }
            }
          }}
        />
      </GrayCard>

      <GrayCard title="Cover Artwork">
        <FlexBox>
          <UploadArea>
            {!musicData.coverImage && (
              <DragNDrop
                setFile={(file) => handleChange("coverImage", file)}
                limit={5}
              />
            )}
            {musicData.coverImage && (
              <UploadAreaImage
                src={URL.createObjectURL(musicData.coverImage)}
                alt="cover-image"
              />
            )}
            {musicData.coverImage && (
              <RemoveFileButton
                onClick={() => handleChange("coverImage", undefined)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M20.0039 9.99951C20.0039 15.5223 15.5267 19.9995 10.0039 19.9995C4.48106 19.9995 0.00390625 15.5223 0.00390625 9.99951C0.00390625 4.47666 4.48106 -0.000488281 10.0039 -0.000488281C15.5267 -0.000488281 20.0039 4.47666 20.0039 9.99951ZM6.97354 6.96916C7.26643 6.67627 7.7413 6.67627 8.03421 6.96916L10.0039 8.93881L11.9735 6.96918C12.2664 6.67629 12.7413 6.67629 13.0342 6.96918C13.3271 7.26207 13.3271 7.73695 13.0342 8.02981L11.0645 9.99951L13.0342 11.9691C13.3271 12.262 13.3271 12.7369 13.0342 13.0298C12.7413 13.3227 12.2664 13.3227 11.9735 13.0298L10.0039 11.0602L8.03421 13.0298C7.74133 13.3227 7.26645 13.3227 6.97356 13.0298C6.68067 12.7369 6.68067 12.262 6.97356 11.9692L8.9432 9.99951L6.97354 8.02981C6.68064 7.73693 6.68064 7.26205 6.97354 6.96916Z"
                    fill="#CDCDCD"
                  />
                </svg>
              </RemoveFileButton>
            )}
          </UploadArea>
          <ArtReqText>
            <Typography weight="semibold" mb="0.8">
              Artwork Requirements
            </Typography>
            <ul
              style={{
                color: Colors.grey500,
                padding: "0",
                paddingLeft: "1rem",
                margin: 0,
                width: "70%"
              }}
            >
              <li>
                <Typography size="s">
                  Cover art must be a square .jpg or .png file
                </Typography>
              </li>
              <li>
                <Typography size="s">
                  Cover art must be 3000 x 3000 px
                </Typography>
              </li>
              <li>
                <Typography size="s">
                  Don’t upload blurry or pixelated artworks
                </Typography>
              </li>
              <li>
                <Typography size="s">
                  Art must not contain website addresses, brand logos, social
                  media handles or logos, any text except artists names and/or
                  name of release
                </Typography>
              </li>
            </ul>
            <Typography size="s" color={Colors.grey500} mt="0.4">
              If it contains any of the above, your release will be rejected by
              stores
            </Typography>
          </ArtReqText>
        </FlexBox>
      </GrayCard>

      <GrayCard title="Release Genre">
        <Typography mb="0.4" weight="medium" color={Colors.grey400}>
          Release Language
        </Typography>
        <TextDropdown
          value={musicData.releaseLanguage}
          items={Languages}
          chosenItem={musicData.releaseLanguage}
          selectItem={(value) => handleChange("releaseLanguage", value)}
          placeholder="what language is your release title in?"
        />
        <Spacer />
        <Typography mb="0.4" weight="medium" color={Colors.grey400}>
          Primary Genre
        </Typography>
        <TextDropdown
          items={MusicGenres}
          value={musicData.primaryGenre}
          chosenItem={musicData.primaryGenre}
          selectItem={(value) => handleChange("primaryGenre", value)}
          placeholder="Select a genre"
        />
        <Spacer />
        <Typography mb="0.4" weight="medium" color={Colors.grey400}>
          Secondary Genre
        </Typography>
        <TextDropdown
          value={musicData.secondaryGenre}
          items={MusicGenres}
          chosenItem={musicData.secondaryGenre}
          selectItem={(value) => handleChange("secondaryGenre", value)}
          placeholder="Select another genre"
        />
      </GrayCard>
    </Container>
  );
};

export default StepOne;

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const Spacer = styled.div`
  height: 2.8rem;
`;

const FlexBox = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 1rem;
  @media only screen and (max-width: 769px) {
    flex-direction: column;
    row-gap: 2.4rem;
  }
`;

const UploadArea = styled.div`
  width: 24.6rem;
  height: 24.6rem;
  position: relative;
  @media only screen and (max-width: 769px) {
    width: 100%;
    height: 30rem;
  }
`;

const UploadAreaImage = styled.img`
  width: 100%;
  height: 100%;
  position: absolute;
  object-fit: cover;
  border-radius: 8px;
  @media only screen and (max-width: 769px) {
    object-fit: contain;
  }
`;

const RemoveFileButton = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  @media only screen and (max-width: 769px) {
    transform: translate(0, -50%);
  }
`;

const ArtReqText = styled.div`
  width: 60%;
  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const ArtistIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Colors.purple200};
`;
